import React, { useEffect, useState, useMemo, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import { useApiQuery } from '../../../../utils/api'
import RaceTrack from './RaceTrack'
import { SprintTitle } from './Announcements'
import WinnerOverlay from './WinnerOverlay'
import GrassBackground from '../../../../assets/grass-background.png'
import BannerTitle from '../../../../assets/banner-title.png'
import competitorsIntroSound from '../../../../assets/competitors_intro.mp3'
import { Loading, Error } from '../../../../components/Elements/Indicators'

// Define interfaces for the component
interface CompetitorEvent {
  type: string
  coins: number
  positionTo: number
  time: number
}

interface Competitor {
  id: string
  accountId?: string // Added accountId property
  name: string
  image: string
  points: number
  bugs: number
  role?: string
  coins?: number
  events?: CompetitorEvent[]
  // Index signature to ensure compatibility with other `Competitor`
  // variants that may include additional dynamic properties.
  [key: string]: unknown
}

interface ColorConfiguration {
  name: string
  baseColor: string
  borderColor: string
  rgbValues: number[]
}

interface CompetitorStyles {
  [competitorId: string]: {
    borderColor: string
    rgbValues: number[]
  }
}

// Fixed: RouteParams now extends Record<string, string | undefined>
interface RouteParams extends Record<string, string | undefined> {
  projKey: string
  boardId: string
  sprintId: string
}

interface SprintData {
  name: string
  users: Array<{
    id: string
    /** display name shown in UI (Jira “displayName”) */
    displayName: string
    accountId: string
    avatarUrl?: string
    role?: string
  }>
}

interface AnalysisUserData {
  id: string
  name: string
  displayName?: string
  accountId?: string    // Added missing property
  avatarUrl?: string    // Added missing property
  resolvedPoints: number
  resolvedBugs: number
}

interface AnalysisData {
  byUser: AnalysisUserData[]
}

type Stage = 'preparing' | 'sprintTitle' | 'introduction' | 'race' | 'winnerReveal'

const startingPositionX = 100 // Starting position for the competitors

// ---------------------------------------------------------------------
// Color configurations with exact RGB values for Lottie animation
// ---------------------------------------------------------------------
const colorConfigurations: ColorConfiguration[] = [
  {
    name: 'royal blue',
    baseColor: '#4169E1',
    borderColor: '#4169E1',
    rgbValues: [0.255, 0.412, 0.882, 1] // Royal blue
  },
  {
    name: 'sky blue',
    baseColor: '#87CEEB',
    borderColor: '#87CEEB',
    rgbValues: [0.529, 0.808, 0.922, 1] // Sky blue
  },
  {
    name: 'gold',
    baseColor: '#FFD700',
    borderColor: '#FFD700',
    rgbValues: [1.0, 0.843, 0.0, 1] // Gold
  },
  {
    name: 'grey',
    baseColor: '#808080',
    borderColor: '#808080',
    rgbValues: [0.502, 0.502, 0.502, 1] // Grey
  },
  {
    name: 'black',
    baseColor: '#000000',
    borderColor: '#000000',
    rgbValues: [0.0, 0.0, 0.0, 1] // Black
  },
  {
    name: 'purple',
    baseColor: '#800080',
    borderColor: '#800080',
    rgbValues: [0.502, 0.0, 0.502, 1] // Purple
  },
  {
    name: 'brown',
    baseColor: '#8B4513',
    borderColor: '#8B4513',
    rgbValues: [0.545, 0.271, 0.075, 1] // Brown
  },
  {
    name: 'orange',
    baseColor: '#FFA500',
    borderColor: '#FFA500',
    rgbValues: [1.0, 0.647, 0.0, 1] // Orange
  },
  {
    name: 'emerald green',
    baseColor: '#228B22',
    borderColor: '#228B22',
    rgbValues: [0.133, 0.545, 0.133, 1] // Darker emerald green (forest green)
  }
]

// --- Helper: Fisher–Yates Shuffle ---
function shuffleArray<T>(array: T[]): T[] {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]]
  }
  return array
}

// --- Main function: Attach schedule to each competitor ---

// Generate race schedules with winner finishing last and better pacing
function attachRaceSchedule(competitors: Competitor[], raceDuration: number = 10): void {
  // Calculate coins for each competitor (points worth double bugs)
  competitors.forEach(competitor => {
    competitor.coins = (competitor.points * 2) + competitor.bugs
  })

  const raceWidth = 1000

  // Sort to identify the winner (highest coins)
  const sortedCompetitors = [...competitors].sort(
    (a, b) => (b.coins ?? 0) - (a.coins ?? 0)
  )
  const winner = sortedCompetitors[0]
  // Calculate step size based on winner's points and bugs - when we take a point step, we move twice as far
  // so a point step is worth 2 bug steps, i.e. 2 stepSizes 
  const stepSizePixels = parseInt((raceWidth / (winner.points * 2 + winner.bugs)).toString())

  // For each competitor, create a race schedule
  competitors.forEach(competitor => {
    const isWinner = competitor.id === winner.id

    // Create events for this competitor
    let events: CompetitorEvent[] = []
    let currentPosition = startingPositionX // Start at 100px to leave some space for the start line

    // Create point events - each worth 2 steps
    for (let i = 0; i < competitor.points; i++) {
      events.push({
        type: 'point',
        coins: 2,
        positionTo: 0,
        time: 0
      })
    }

    // Create bug events - each worth 1 step
    for (let i = 0; i < competitor.bugs; i++) {
      events.push({
        type: 'bug',
        coins: 1,
        positionTo: 0,
        time: 0
      })
    }

    // Shuffle events to randomize a bit
    events = shuffleArray(events)

    // add calculated positions to the events
    // Fixed: Removed unused index variable
    events.forEach(event => {
      if (event.type === 'point') {
        currentPosition += stepSizePixels * 2
        event.positionTo = currentPosition
      } else {
        currentPosition += stepSizePixels
        event.positionTo = currentPosition
      }
    })

    // Ensure winner reaches the finish line (approximately 1080px based on 90% of 1200px container)
    if (isWinner && events.length > 0) {
      const finishLinePosition = 1080 // 90% of container width (1200px)
      const lastEvent = events[events.length - 1]

      // If the winner's final position is short of the finish line, extend it
      if (lastEvent.positionTo < finishLinePosition) {
        lastEvent.positionTo = finishLinePosition
      }
    }

    // Calculate total events
    const totalEvents = competitor.points + competitor.bugs
    const timePerEvent = raceDuration / totalEvents

    // loop through events and add the time, with some randomization
    events.forEach((event, idx) => {
      event.time = timePerEvent * (idx + 1)
      // add variance of 30% to the time, which goes from -15% to +15%
      if (idx === totalEvents - 1) {
        //if its the last event only subtract variance, and do not touch if winner so that winner goes last
        if (!isWinner) {
          event.time -= timePerEvent * 0.2 + (timePerEvent * (Math.random() * 0.15))
        }
      } else {
        event.time += timePerEvent * (Math.random() * 0.3 - 0.15)
      }
    })

    // Attach events to competitor
    competitor.events = events
  })
}

// ---------------------------------------------------------------------
// Main RaceSimulation Component
// ---------------------------------------------------------------------
const RaceSimulation: React.FC = () => {
  // Stages: 'preparing', 'sprintTitle', 'introduction', 'race', 'winnerReveal'
  const [stage, setStage] = useState<Stage>('preparing')
  const [winner, setWinner] = useState<Competitor | null>(null)
  const [raceKey, setRaceKey] = useState<number>(0)
  const [assetsReady, setAssetsReady] = useState<boolean>(false)
  const introSoundRef = useRef<HTMLAudioElement | null>(null) // Create a ref for the intro sound
  const navigate = useNavigate()
  const { projKey, boardId, sprintId } = useParams<RouteParams>()
  // Unified API hook

  // Fetch sprint data
  const {
    isLoading: isLoadingSprint,
    error: errorSprint,
    data: sprint
  } = useApiQuery<SprintData>(`/sprints/${sprintId}`)

  // Fetch sprint analysis data
  const {
    isLoading: isLoadingAnalysis,
    error: errorAnalysis,
    data: analysis
  } = useApiQuery<AnalysisData>(`/analysis/sprints/${sprintId}`, { enabled: !!sprint })

  // Prepare competitors with API data
  const competitorsWithSchedules = useMemo<Competitor[]>(() => {
    if (!sprint || !analysis) return []

    // Combine user data from sprint with analysis metrics
    const competitors = analysis.byUser.map(userAnalysis => {
      // Fixed: Match on accountId instead of id
      const user = sprint.users.find(u => u.accountId === userAnalysis.id)

      return {
        id: userAnalysis.id || userAnalysis.accountId || `fallback-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        accountId: userAnalysis.accountId || userAnalysis.id, // Add accountId for funnyTitles
        name: userAnalysis.name || userAnalysis.displayName || user?.displayName || 'Unknown User',
        // Fixed: Use avatarUrl from userAnalysis (analysis data) which has the actual Jira avatars
        image: userAnalysis.avatarUrl || user?.avatarUrl || `https://placehold.co/100/blue/white?text=${(userAnalysis.displayName || userAnalysis.name || user?.displayName || 'U').charAt(0)}`,
        points: userAnalysis.resolvedPoints || 0,
        bugs: userAnalysis.resolvedBugs || 0,
        role: user?.role || 'Developer'
      }
    })
      .filter(competitor => competitor.points > 0 || competitor.bugs > 0)
      // Sort by points*2 + bugs in descending order
      .sort((a, b) => ((b.points * 2 + b.bugs) - (a.points * 2 + a.bugs)))
      // Take only the top 7 competitors
      .slice(0, 7)

    // Attach race schedules
    attachRaceSchedule(competitors, 10) // 10 seconds race duration
    return competitors
  }, [sprint, analysis])

  // Assign unique color styles to competitors
  const competitorStyles = useMemo<CompetitorStyles>(() => {
    if (!competitorsWithSchedules.length) return {}

    // Create a copy of the array to shuffle
    const availableColors = [...colorConfigurations]

    // Fisher-Yates shuffle algorithm
    for (let i = availableColors.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [availableColors[i], availableColors[j]] = [availableColors[j], availableColors[i]]
    }

    // Create a map of competitor IDs to color styles
    const styleMap: CompetitorStyles = {}
    competitorsWithSchedules.forEach((competitor, index) => {
      const colorConfig = availableColors[index % availableColors.length]
      styleMap[competitor.id] = {
        borderColor: colorConfig.borderColor,
        rgbValues: colorConfig.rgbValues // Pass the RGB values to the component
      }
    })

    return styleMap
  }, [competitorsWithSchedules])

  // Determine the winner based on the highest coins
  useEffect(() => {
    if (competitorsWithSchedules.length > 0) {
      const sorted = [...competitorsWithSchedules].sort((a, b) => (b.coins || 0) - (a.coins || 0))
      setWinner(sorted[0])
    }
    // Fixed: Added missing return statement
    return () => { }
  }, [competitorsWithSchedules])

  // Wait for competitors to be processed before starting animations
  useEffect(() => {
    if (competitorsWithSchedules.length > 0 && stage === 'preparing') {
      // Small delay to ensure everything is set up
      const timer: NodeJS.Timeout = setTimeout(() => {
        setAssetsReady(true)
        setStage('sprintTitle')
      }, 500)
      return () => clearTimeout(timer)
    }
    // Fixed: Added missing return statement
    return () => { }
  }, [competitorsWithSchedules, stage])

  // Stage transitions - only after preparations complete
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined
    if (stage === 'sprintTitle' && assetsReady) {
      // Create and play the competitors intro sound
      introSoundRef.current = new Audio(competitorsIntroSound)
      introSoundRef.current
        .play()
        .catch(err => console.error('Audio play failed:', err))

      // No timer needed here anymore - we'll use the callback
    }
    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [stage, assetsReady])

  // Clean up sounds when component unmounts - MOVED UP HERE
  useEffect(() => {
    return () => {
      if (introSoundRef.current) {
        introSoundRef.current.pause()
        introSoundRef.current.currentTime = 0
      }
    }
  }, [])

  // Handle sprint title completion
  const handleTitleComplete = (): void => {
    setStage('introduction')
  }

  const sprintName = sprint?.name || `Sprint ${sprintId}`

  // Loading and error states
  if (isLoadingSprint || isLoadingAnalysis) {
    // Fixed: Changed title prop to message
    return <Loading message="Loading sprint data" />
  }

  if (errorSprint || errorAnalysis) {
    // Fixed: Proper error message handling with optional chaining and nullish coalescing
    // Cast to Error | undefined so TS knows `message` exists
    const errMsg =
      (errorSprint as Error | undefined)?.message ??
      (errorAnalysis as Error | undefined)?.message ??
      'Error loading data'
    return <Error message={errMsg} />
  }

  if (competitorsWithSchedules.length === 0) {
    return <Error message="There are no users with resolved points or bugs in this sprint." />
  }

  // Reset the race
  const replayRace = (): void => {
    setRaceKey(prev => prev + 1)
    setAssetsReady(false)
    setStage('preparing')
  }

  // Navigate to results
  const handleSeeSprintResults = (): void => {
    navigate(`/projects/${projKey}/boards/${boardId}/sprints/${sprintId}/analysis`)
  }

  // Handle race completion
  const handleRaceFinished = (): void => {
    setStage('winnerReveal')
  }

  // Handle introductions completion and stop intro sound with fade out
  const handleIntroductionsComplete = (): void => {
    // Fade out the intro sound over 2 seconds when introductions are complete
    if (introSoundRef.current) {
      const fadeOutDuration = 6000 // 2 seconds
      const startVolume = introSoundRef.current.volume
      const fadeOutStep = 20 // Update every 20ms

      // Save the start time
      const startTime = Date.now()

      // Create an interval to gradually decrease volume
      const fadeInterval = setInterval(() => {
        const elapsedTime = Date.now() - startTime
        const newVolume = startVolume * (1 - elapsedTime / fadeOutDuration)

        // Set the new volume, ensuring it doesn't go below 0
        if (introSoundRef.current) {
          introSoundRef.current.volume = Math.max(0, newVolume)

          // Once the fade is complete or nearly complete
          if (elapsedTime >= fadeOutDuration) {
            clearInterval(fadeInterval)
            introSoundRef.current.pause()
            introSoundRef.current.currentTime = 0
            introSoundRef.current.volume = startVolume // Reset volume for future use
          }
        }
      }, fadeOutStep)
    }

    setStage('race')
  }

  return (
    <div className="w-full h-screen flex justify-center overflow-hidden">
      <div className="relative w-[1200px] h-[1100px]">
        {/* Green background - always present */}
        <div
          className="absolute rounded-2xl"
          style={{
            top: '115px', // Match the border's top position
            left: '0',
            right: '0',
            bottom: '0',
            backgroundImage: `url(${GrassBackground})`,
            backgroundRepeat: 'repeat',
            zIndex: -10  // Already set to stay behind everything
          }}
        ></div>

        {/* Banner title above the border */}
        <div
          className="absolute left-1/2 transform -translate-x-1/2"
          style={{
            top: '0px',
            width: '397px',
            height: '200px',
            backgroundImage: `url(${BannerTitle})`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            zIndex: 1100 // Above everything else
          }}
        ></div>

        {/* Wooden border with specified colors */}
        <div
          className="absolute pointer-events-none rounded-lg"
          style={{
            top: '115px',
            left: '0',
            right: '0',
            bottom: '0',
            border: '2px solid #733800',  // Outer border (1px)
            boxSizing: 'border-box',
            zIndex: 1000
          }}
        >
          <div
            className="absolute inset-0"
            style={{
              border: '5px solid #d78317',  // Middle border (5px)
              boxSizing: 'border-box',
            }}
          >
            <div
              className="absolute inset-0 "
              style={{
                border: '2px solid #8c4f0e',  // Inner border (1px)
                boxSizing: 'border-box',
              }}
            ></div>
          </div>
        </div>

        {/* Sprint Title */}
        <AnimatePresence>
          {stage === 'sprintTitle' && (
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <SprintTitle key="sprintTitle" title={sprintName} onComplete={handleTitleComplete} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Race Track - handles introduction, countdown and racing */}
        {stage !== 'sprintTitle' && stage !== 'winnerReveal' && (
          <RaceTrack
            key={`raceTrack-${raceKey}`}
            competitors={competitorsWithSchedules}
            introducedCount={competitorsWithSchedules.length}
            raceStarted={stage === 'race'}
            winner={winner}
            startingPositionX={startingPositionX}
            onRaceFinished={handleRaceFinished}
            onIntroductionsComplete={handleIntroductionsComplete} // Updated to use our new handler
            competitorStyles={competitorStyles} // Pass the styles down
          />
        )}

        {/* Winner overlay */}
        <AnimatePresence>
          {stage === 'winnerReveal' && winner && (
            <WinnerOverlay
              key="winnerReveal"
              winner={winner}
              onReplay={replayRace}
              onSeeResults={handleSeeSprintResults}
              borderColor={winner ? competitorStyles[winner.id].borderColor : '#8a2be2'}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default RaceSimulation
